import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Loader2, LogOut, Download, Lock, Check } from "lucide-react";
import { toast } from "sonner";
import { useToolsAuth } from "@/context/ToolsAuthContext";
import { toolsApi, formatApiError, goToCheckout } from "@/lib/toolsApi";

const AuthCard = () => {
  const { login, register } = useToolsAuth();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", first_name: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "register") await register(form.email, form.password, form.first_name);
      else await login(form.email, form.password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="downloads-auth">
      <div className="text-center mb-8">
        <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-3">Your Library</p>
        <h1 className="font-serif font-light text-3xl md:text-4xl text-wine">
          {mode === "register" ? "Create your free account" : "Welcome back"}
        </h1>
        <p className="mt-3 text-sm text-charcoal/60">
          Your downloads live here — sign in to buy or re-download anytime.
        </p>
      </div>
      <form onSubmit={submit} className="space-y-6 border border-gold/40 bg-white/50 p-8" data-testid="downloads-auth-form">
        {mode === "register" && (
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">First name (optional)</label>
            <input
              type="text"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              data-testid="downloads-firstname-input"
              className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
            />
          </div>
        )}
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Email</label>
          <input
            required
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="downloads-email-input"
            className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-charcoal/60">Password</label>
          <input
            required
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="downloads-password-input"
            className="w-full bg-transparent border-b-2 border-gold/50 py-3 mt-2 text-charcoal focus:outline-none focus:border-terracotta transition-colors"
          />
          {mode === "register" && <p className="mt-2 text-xs text-charcoal/50">At least 8 characters.</p>}
        </div>
        {error && <p className="text-sm text-destructive" data-testid="downloads-auth-error">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="downloads-auth-submit"
          className="w-full rounded-full bg-terracotta text-cream px-8 py-4 text-sm font-medium tracking-wide hover:bg-wine transition-colors duration-300 disabled:opacity-60 inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader2 size={16} className="animate-spin" />}
          {mode === "register" ? "Create account" : "Sign in"}
        </button>
        <p className="text-center text-sm text-charcoal/60">
          {mode === "register" ? "Already have an account?" : "Need an account?"}{" "}
          <button
            type="button"
            onClick={() => {
              setMode(mode === "register" ? "login" : "register");
              setError("");
            }}
            data-testid="downloads-auth-toggle"
            className="text-terracotta underline underline-offset-2 hover:text-wine"
          >
            {mode === "register" ? "Sign in" : "Create one"}
          </button>
        </p>
      </form>
    </div>
  );
};

const ProductRow = ({ product, onRefresh }) => {
  const [busy, setBusy] = useState(false);

  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await toolsApi.post("/pdfs/checkout", {
        product_id: product.id,
        origin_url: window.location.origin,
      });
      goToCheckout(data.checkout_url);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  const download = async () => {
    setBusy(true);
    try {
      const res = await toolsApi.get(`/pdfs/${product.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${product.name}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      if (product.free) onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const claimFree = async () => {
    setBusy(true);
    try {
      await toolsApi.post("/pdfs/claim-free", { product_id: product.id });
      await download();
      onRefresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={`library-product-${product.id}`}
      className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between rounded-2xl border border-gold/40 bg-white/50 p-6"
    >
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <h3 className="font-serif text-lg text-wine">{product.name}</h3>
          {product.owned && (
            <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-terracotta bg-blush/50 rounded-full px-2 py-0.5">
              <Check size={11} /> Owned
            </span>
          )}
        </div>
        <p className="mt-2 text-sm text-charcoal/70 font-light">{product.description}</p>
      </div>
      <div className="shrink-0">
        {!product.available ? (
          <span className="text-xs uppercase tracking-[0.2em] text-charcoal/45" data-testid={`library-soon-${product.id}`}>
            Currently unavailable
          </span>
        ) : product.owned ? (
          <button
            onClick={download}
            disabled={busy}
            data-testid={`library-download-${product.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Download
          </button>
        ) : product.free ? (
          <button
            onClick={claimFree}
            disabled={busy}
            data-testid={`library-getfree-${product.id}`}
            className="inline-flex items-center gap-2 rounded-full bg-terracotta text-cream px-6 py-3 text-sm font-medium hover:bg-wine transition-colors disabled:opacity-60"
          >
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Get it free
          </button>
        ) : (
          <button
            onClick={buy}
            disabled={busy}
            data-testid={`library-buy-${product.id}`}
            className="inline-flex items-center gap-2 rounded-full border border-terracotta text-terracotta px-6 py-3 text-sm font-medium hover:bg-terracotta hover:text-cream transition-colors disabled:opacity-60"
          >
            <Lock size={14} /> Buy {product.price_label}
          </button>
        )}
      </div>
    </div>
  );
};

export default function Downloads() {
  const { user, loading, logout } = useToolsAuth();
  const [products, setProducts] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await toolsApi.get("/pdfs");
      setProducts(data);
    } catch {
      setProducts([]);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-cream" data-testid="downloads-loading">
        <Loader2 className="animate-spin text-terracotta" size={28} />
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-cream pt-32 pb-24 px-6 md:px-12" data-testid="downloads-page">
      <div className="max-w-3xl mx-auto">
        {!user ? (
          <AuthCard />
        ) : (
          <>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="flex flex-wrap items-center justify-between gap-4 mb-10 border-b border-gold/30 pb-6"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-terracotta">Your Library</p>
                <h1 className="mt-2 font-serif font-light text-3xl md:text-4xl text-wine">Your downloads</h1>
                <p className="mt-1 text-sm text-charcoal/60" data-testid="downloads-account-email">{user.email}</p>
              </div>
              <button
                onClick={logout}
                data-testid="downloads-logout"
                className="inline-flex items-center gap-2 text-sm text-charcoal/60 hover:text-wine transition-colors"
              >
                <LogOut size={15} /> Sign out
              </button>
            </motion.div>

            {products === null ? (
              <div className="flex justify-center py-16">
                <Loader2 className="animate-spin text-terracotta" size={24} />
              </div>
            ) : (
              <div className="space-y-4">
                {products.map((p) => (
                  <ProductRow key={p.id} product={p} onRefresh={load} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
